import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
    Card,
    Stack,
    Container,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Box,
    Link,
    Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 600,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

const INVESTOR_TYPES = [
    { value: 'individual', label: 'Individual / Angel' },
    { value: 'family_office', label: 'Family Office' },
    { value: 'institutional', label: 'Institutional' },
    { value: 'corporate', label: 'Corporate / Strategic' },
    { value: 'other', label: 'Other' },
];

const steps = ['Basic Info', 'Profile', 'NDA Agreement'];

export default function InvestorRegister() {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);

    const RegisterSchema = Yup.object().shape({
        first_name: Yup.string().max(50, 'Max 50 characters').required('First name is required'),
        last_name: Yup.string().max(50, 'Max 50 characters').required('Last name is required'),
        email: Yup.string().email('Must be a valid email').required('Email is required'),
        password: Yup.string()
            .min(8, 'Minimum 8 characters')
            .matches(/[A-Z]/, 'Must contain an uppercase letter')
            .matches(/[a-z]/, 'Must contain a lowercase letter')
            .matches(/[0-9]/, 'Must contain a digit')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain a special character')
            .required('Password is required'),
        confirm_password: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
            .required('Confirm your password'),
        phone: Yup.string()
            .matches(/^\+?[0-9\s\-()]{7,20}$/, 'Enter a valid phone number')
            .required('Phone number is required'),
        investor_type: Yup.string(),
        organization_name: Yup.string().max(255),
        linkedin_url: Yup.string().url('Must be a valid URL'),
        nda_accepted: Yup.boolean().oneOf([true], 'You must accept the NDA to continue'),
    });

    const formik = useFormik({
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirm_password: '',
            phone: '',
            country_id: null,
            investor_type: '',
            organization_name: '',
            linkedin_url: '',
            nda_accepted: false,
        },
        validationSchema: RegisterSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                await syndicationApi.post('investor/register/', values);
                setSuccess(true);
            } catch (error) {
                const msg = error.response?.data?.message
                    || error.response?.data?.email?.[0]
                    || 'Registration failed. Please try again.';
                setApiError(msg);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const { errors, touched, isSubmitting, handleSubmit, getFieldProps, values, setFieldValue } = formik;

    const handleNext = () => {
        // Validate current step fields before proceeding
        const stepFields = {
            0: ['first_name', 'last_name', 'email', 'password', 'confirm_password'],
            1: ['phone'],
            2: ['nda_accepted'],
        };
        const currentFields = stepFields[activeStep];
        const hasErrors = currentFields.some((f) => !values[f] || errors[f]);

        // Touch current fields to show errors
        currentFields.forEach((f) => formik.setFieldTouched(f, true));

        // Validate before moving
        formik.validateForm().then((formErrors) => {
            const stepHasErrors = currentFields.some((f) => formErrors[f]);
            if (!stepHasErrors) {
                setActiveStep((prev) => prev + 1);
            }
        });
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    if (success) {
        return (
            <RootStyle title="Registration Successful">
                <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                    <FormCard>
                        <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">Registration Successful!</Typography>
                            <Typography variant="body1" color="text.secondary">
                                Please check your email inbox for a verification link.
                                You must verify your email before your account can be reviewed.
                            </Typography>
                            <Divider sx={{ width: '100%' }} />
                            <Link component={RouterLink} to="/investor/login" variant="subtitle1">
                                Go to Login
                            </Link>
                        </Stack>
                    </FormCard>
                </Container>
            </RootStyle>
        );
    }

    return (
        <RootStyle title="Investor Registration | Gateway X Syndication">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Stack spacing={3}>
                        <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <Typography variant="h4" gutterBottom>Investor Registration</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Gateway X Syndication Platform
                            </Typography>
                        </Box>

                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {apiError && <Alert severity="error">{apiError}</Alert>}

                        <FormikProvider value={formik}>
                            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                                {/* Step 1: Basic Info */}
                                {activeStep === 0 && (
                                    <Stack spacing={2.5}>
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                fullWidth
                                                label="First Name"
                                                {...getFieldProps('first_name')}
                                                error={Boolean(touched.first_name && errors.first_name)}
                                                helperText={touched.first_name && errors.first_name}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Last Name"
                                                {...getFieldProps('last_name')}
                                                error={Boolean(touched.last_name && errors.last_name)}
                                                helperText={touched.last_name && errors.last_name}
                                            />
                                        </Stack>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            type="email"
                                            {...getFieldProps('email')}
                                            error={Boolean(touched.email && errors.email)}
                                            helperText={touched.email && errors.email}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Password"
                                            type={showPassword ? 'text' : 'password'}
                                            {...getFieldProps('password')}
                                            error={Boolean(touched.password && errors.password)}
                                            helperText={touched.password && errors.password}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                            <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Confirm Password"
                                            type={showConfirm ? 'text' : 'password'}
                                            {...getFieldProps('confirm_password')}
                                            error={Boolean(touched.confirm_password && errors.confirm_password)}
                                            helperText={touched.confirm_password && errors.confirm_password}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                                                            <Icon icon={showConfirm ? eyeFill : eyeOffFill} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <LoadingButton fullWidth size="large" variant="contained" onClick={handleNext}>
                                            Next
                                        </LoadingButton>
                                    </Stack>
                                )}

                                {/* Step 2: Profile Details */}
                                {activeStep === 1 && (
                                    <Stack spacing={2.5}>
                                        <TextField
                                            fullWidth
                                            label="Phone Number"
                                            placeholder="+1 (555) 000-0000"
                                            {...getFieldProps('phone')}
                                            error={Boolean(touched.phone && errors.phone)}
                                            helperText={touched.phone && errors.phone}
                                        />
                                        <TextField
                                            fullWidth
                                            select
                                            label="Investor Type"
                                            {...getFieldProps('investor_type')}
                                        >
                                            <MenuItem value="">-- Select --</MenuItem>
                                            {INVESTOR_TYPES.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            fullWidth
                                            label="Organization Name"
                                            {...getFieldProps('organization_name')}
                                            helperText="Optional"
                                        />
                                        <TextField
                                            fullWidth
                                            label="LinkedIn Profile URL"
                                            {...getFieldProps('linkedin_url')}
                                            error={Boolean(touched.linkedin_url && errors.linkedin_url)}
                                            helperText={(touched.linkedin_url && errors.linkedin_url) || 'Optional'}
                                        />
                                        <Stack direction="row" spacing={2}>
                                            <LoadingButton fullWidth size="large" variant="outlined" onClick={handleBack}>
                                                Back
                                            </LoadingButton>
                                            <LoadingButton fullWidth size="large" variant="contained" onClick={handleNext}>
                                                Next
                                            </LoadingButton>
                                        </Stack>
                                    </Stack>
                                )}

                                {/* Step 3: NDA Agreement */}
                                {activeStep === 2 && (
                                    <Stack spacing={2.5}>
                                        <Box
                                            sx={{
                                                maxHeight: 250,
                                                overflow: 'auto',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                p: 2,
                                                bgcolor: 'grey.50',
                                            }}
                                        >
                                            <Typography variant="subtitle2" gutterBottom>
                                                Non-Disclosure Agreement
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                                By registering on the Gateway X Syndication Platform, you agree to maintain
                                                the confidentiality of all deal information, financial data, company details,
                                                and investment materials shared through this platform. You shall not disclose,
                                                reproduce, or distribute any confidential information to third parties without
                                                prior written consent from Gateway X. Violation of this agreement may result
                                                in legal action and immediate termination of platform access.
                                            </Typography>
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.nda_accepted}
                                                    onChange={(e) => setFieldValue('nda_accepted', e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    I have read and agree to the Non-Disclosure Agreement
                                                </Typography>
                                            }
                                        />
                                        {touched.nda_accepted && errors.nda_accepted && (
                                            <Typography variant="caption" color="error">
                                                {errors.nda_accepted}
                                            </Typography>
                                        )}
                                        <Stack direction="row" spacing={2}>
                                            <LoadingButton fullWidth size="large" variant="outlined" onClick={handleBack}>
                                                Back
                                            </LoadingButton>
                                            <LoadingButton
                                                fullWidth
                                                size="large"
                                                type="submit"
                                                variant="contained"
                                                loading={isSubmitting}
                                                disabled={!values.nda_accepted}
                                            >
                                                Register
                                            </LoadingButton>
                                        </Stack>
                                    </Stack>
                                )}
                            </Form>
                        </FormikProvider>

                        <Divider />
                        <Typography variant="body2" align="center" color="text.secondary">
                            Already have an account?{' '}
                            <Link component={RouterLink} to="/investor/login" variant="subtitle2">
                                Sign In
                            </Link>
                        </Typography>
                    </Stack>
                </FormCard>
            </Container>
        </RootStyle>
    );
}
